// TimelineEngine — pure helpers for resolving a song timeline at a given time.
// No React, no side effects. The audio player's currentTime is the only clock.

import { Song, TimelineEvent, Zone } from "@/models/types";

export interface ResolvedTimeline {
  section: string;
  chord: string;
  activeZoneId: string;
  activeMessage: string | null;
}

/** Walk events up to `time` accumulating the latest section/chord/zone. */
export function resolveAt(song: Song, time: number): ResolvedTimeline {
  let section = "Intro";
  let chord = song.progression[0] ?? "Am";
  let activeZoneId = Object.keys(song.zones)[0] ?? "A";
  let activeMessage: string | null = null;

  for (const e of song.timeline) {
    if (e.time > time + 1e-6) break;
    if (e.section) section = e.section;
    if (e.chord) chord = e.chord;
    if (e.activeZone) activeZoneId = e.activeZone;
    // a message stays "fresh" for ~2.4s after its event
    if (e.message) {
      activeMessage = time - e.time <= 2.4 ? e.message : null;
    }
  }
  return { section, chord, activeZoneId, activeMessage };
}

export interface NextTransition {
  event: TimelineEvent | null;
  prepareStart: number;
}

const PREPARE_BARS = 3; // anticipation window before a transition

export function findNextTransition(song: Song, time: number): NextTransition {
  const beat = 60 / song.bpm;
  const bar = beat * song.timeSignature[0];
  for (const e of song.timeline) {
    if (e.transition && e.time > time) {
      return { event: e, prepareStart: e.time - PREPARE_BARS * bar };
    }
  }
  return { event: null, prepareStart: Infinity };
}

export function zoneById(song: Song, id: string | undefined): Zone | null {
  if (!id) return null;
  return song.zones[id] ?? null;
}
